<template>
    <!-- Meter column owns the canvas-backed dial (renderer + dark-mode
         resync), the per-phase Progress overlay, and the square-aspect
         wrapper. The composition root owns cross-cell layout — Vue
         scoped CSS stamps the child root with the parent's `data-v-*`
         attribute, so `.results-card .meter-col` still matches here.

         The under-meter `.phase-progress` is the CURRENT-PHASE bar
         (single colour = active phase; fills 0→100 as that phase
         progresses; resets on phase boundary). The OVERALL multi-phase
         progress lives at the bottom GlassTimeline. On the DPI variant
         (which collapses `ping` out) the active state lights the first
         visible latency cell (`jitter`) instead. -->
    <section
        ref="rootEl"
        class="meter-col"
        :style="{ '--dial-diameter-ratio': DIAL_DIAMETER_RATIO }"
    >
        <div class="meter-inner">
            <!-- Idle reads as dial-with-sweep + Pulse aura + aurora drift;
                 no separate Metaballs backdrop (retired per AK-W5-α /
                 G-AK-D1 — FD1 §3.1 TRANSPOSITION 1 + A5 §4.1 already name
                 the system facts this layer was duplicating). -->
            <div class="speedtest relative h-full w-full">
                <canvas ref="meter" class="absolute inset-0 h-full w-full" />
            </div>

            <!-- AK-FD1 §2.3 — running-state placeholder for the
                 phase-start → first-data gap. The engine has STARTED
                 (`isRunning`) but no sample has landed (`data == null`),
                 so the under-meter region reads as a dead-blank rail. A
                 glass-ui `<Skeleton variant="breath">` (the 6s opacity
                 breath synced to `--animate-ambient-pulse-duration`) fills
                 that brief window: the breath register reads "the surface
                 is alive and resolving", not "the screen is stuck" — the
                 right vocabulary for a KNOWN-IMMINENT wait. It retires the
                 instant the first sample arrives (`!speedtest.data`
                 flips). Composed from the EXISTING breath variant — no new
                 primitive; the variant's own PRM bracket pins it to the
                 breath trough under `prefers-reduced-motion`. -->
            <Skeleton
                v-if="isPreDataWindow"
                variant="breath"
                class="meter-prewarm"
                aria-hidden="true"
            />
        </div>

        <!-- `variant="gradient"` is glass-ui's single-rail variant; the
             consumer paints the indicator's fill via `--progress-fill`
             (see `.phase-progress` below) keyed off the chassis
             `--phase-color` cascade — no JS computed, no :deep reach, no
             keyframe animation. SmoothProgress already drives
             `currentPhaseProgressPct`.

             W17 (D3/D8): the `:disable-crescendo` prop the pre-W17
             template passed was REMOVED at glass-ui 3.10.0 (the prop no
             longer exists on `ProgressGradient` — published-dep-drift), so
             it was a dead no-op. The crescendo overlay's transparent
             `background-image` longhand is now handled at the fill layer:
             `--progress-fill` is a FLAT SOLID phase colour so it lands on
             the indicator `background-color`, which the crescendo's
             `screen` blend reveals (see the `.phase-progress` fill note).
             The crescendo-clobber fix proper is routed
             (ASK-GU-PROGRESS-CRESCENDO-CLOBBER). -->
        <Progress
            variant="gradient"
            :model-value="currentPhaseProgressPct"
            class="phase-progress"
        />
    </section>
</template>

<script setup lang="ts">
import {
    ref,
    reactive,
    computed,
    onMounted,
    onUnmounted,
    useTemplateRef,
    watch,
} from "vue";
import { Progress } from "@mkbabb/glass-ui/progress";
import { Skeleton } from "@mkbabb/glass-ui";
import {
    DAMPING,
    SNAP_THRESHOLD,
    useAnimatedNumber,
} from "@mkbabb/glass-ui/motion";
import { useIdleReady } from "@mkbabb/glass-ui/dom";
import { installDarkModeSync } from "@mkbabb/glass-ui/dark";
import { clamp } from "@mkbabb/value.js";
import { useMeterRenderer } from "../composables/useMeterRenderer.ts";
import type { MeterRendererProps } from "../composables/useMeterRenderer.ts";
import { DIAL_DIAMETER_RATIO } from "./meter/canvas/meter/config.ts";
import { handoffRemap } from "../composables/meter/math.ts";
import { useSpeedtestStore } from "@features/speedtest/state/index.ts";
import { usePhaseTransition } from "../composables/usePhaseTransition.ts";

// T2 — prop-chain deleted; progress is store-derived at the leaf (phaseBarTarget below).
// TraditionalSpeedtest → SpeedtestResults → MeterColumn was pure forwarding; no per-level
// transform existed. The store is already imported above (useSpeedtestStore).

// W2-β-FIX — this component's `<template>` opens with a top-level HTML
// comment before the `<section class="meter-col">` root, so Vue treats the
// instance as a fragment: its public `$el` resolves to that leading comment
// anchor (a `#comment` node), NOT the `.meter-col` Element. The W12-γ
// container morph read `meterColumn.$el` as its morph SOURCE and called
// `getBoundingClientRect()` on it — which a comment node does not have —
// throwing inside the same reactive flush that flips the card to `complete`
// and stranding the user on the running meter. The fix is to expose the
// REAL `.meter-col` Element via a template ref so the morph never sees the
// fragment anchor. `SpeedtestResults.vue` reads `meterColumn.rootEl`.
const rootEl = useTemplateRef<HTMLElement>("rootEl");
defineExpose({ rootEl });

const speedtest = useSpeedtestStore();

// AK-FD1 §2.3 — the phase-start → first-data window. The engine has
// STARTED (`isRunning`) but no live sample has landed yet (`data` is the
// engine's `Ref<LiveTestData | null>`, null until the first emission), so
// the meter region reads as a dead-blank rail. While this is true the
// `<Skeleton variant="breath">` placeholder paints; it retires the instant
// the first sample flips `data` non-null. Reading the store's `data` ref
// directly (rather than re-instantiating `useHeroVariant`'s smoother graph)
// keeps the gate local to the meter cell.
const isPreDataWindow = computed(
    () => speedtest.isRunning && speedtest.data == null,
);

// ── Canvas-backed dial ─────────────────────────────────────────────────

const meterProps = reactive<MeterRendererProps>({
    get speedtestData() {
        return speedtest.data;
    },
    get currentEnginePhase() {
        return speedtest.currentEnginePhase;
    },
    get isRunning() {
        return speedtest.isRunning;
    },
    getSpeedtestStateAmount: speedtest.getSpeedtestStateAmount,
});

// Vue 3.5 idiom (FA-2 §3.1 / AH.W5-b.2d): the template ref binds via
// `useTemplateRef("meter")` against `<canvas ref="meter" />`, retiring
// the pre-3.5 `ref<HTMLCanvasElement | null>(null)` + bare-name shape.
const meterCanvas = useTemplateRef<HTMLCanvasElement>("meter");

const renderer = useMeterRenderer(meterCanvas, meterProps);

// T5 / AUA-F7 off-screen park — the published glass-ui pattern (used
// internally by `useCanvas2D`): with `content-visibility: auto` on the
// `.meter-col` host (see scoped CSS), the browser fires
// `contentvisibilityautostatechange` whenever the column enters or leaves
// the rendering-skipped region as it scrolls. `e.skipped === true` means
// the column is off-screen → suspend the breath loop (zero rAF); `false`
// means it re-entered → the renderer resumes it (gated against an active
// completion/abort play). This is the scroll-out half of the park; the
// lib's `pauseWhenHidden` covers tab-hidden only. No IntersectionObserver
// fallback, no custom scheduler (inv-16) — the native event IS the gate.
function onContentVisibilityChange(
    e: ContentVisibilityAutoStateChangeEvent,
): void {
    renderer.setOffscreen(e.skipped);
}

// DESIGN-INTAKE-3 N1 — first-paint-then-arm. The meter renderer's
// `initialize()` is the EAGER mount-time arm the live-instance audit read
// as "loading a speedtest in the background": it recomputes the canvas
// geometry, paints the first frame, and spins the breath rAF loop —
// synchronous work on the critical path BEFORE FCP settles (witnessed in
// `research/INTAKE-3-evidence/`: the engine/IP fetch fired ~6ms BEFORE the
// dev FCP mark). The auto-start TRIGGER (`useAutoStart`) is already
// correctly FCP-gated and stays untouched; this gates the disjoint UPSTREAM
// beat — the renderer ARM — behind the same idiomatic ready primitive the
// aurora WebGL boot in `App.vue` rides (`useIdleReady`, R0G-3 / the
// sanctioned post-paint rIC sequencer per `feedback_router_isready_sequencer`).
// `onReady` fires exactly once on the first idle tick after mount (rIC, with
// the Safari `setTimeout(0)` fallback), so the arm lands AFTER first paint —
// NO polling loop. The contentvisibility listener stays at mount (a cheap
// `addEventListener`; `setOffscreen` is a no-op pre-init because `startLoop`/
// `stopLoop` schedule/clear a tick that bails on `!bag.config`). The
// Fast.com auto-run semantic is preserved end-to-end: once armed, the
// already-gated trigger still kicks the test off automatically.
useIdleReady({ onReady: () => renderer.initialize() });

onMounted(() => {
    rootEl.value?.addEventListener(
        "contentvisibilityautostatechange",
        onContentVisibilityChange,
    );
});
onUnmounted(() => {
    rootEl.value?.removeEventListener(
        "contentvisibilityautostatechange",
        onContentVisibilityChange,
    );
    renderer.dispose();
});

// Re-initialize meter when dark mode toggles. installDarkModeSync owns the
// two-step timing contract (await Vue class application, then await
// paint) so the canvas reads the new computed CSS variable values
// cleanly.
installDarkModeSync(() => {
    renderer.dispose();
    renderer.initialize();
});

// ── Current-phase bar state (user 2026-05-13 redress) ─────────────────
//
// The under-meter bar shows ONLY the active phase: single colour = the
// current MetricDef.color, fill = current-test progress (0..1 → 0..100).
// Resets on phase boundary. The OVERALL phase progress (all 4 metrics
// with state colouring) is the bottom GlassTimeline's responsibility.

// ── Phase progress smoother ────────────────────────────────────────────
//
// Consumes keyframes.js SmoothProgress (via glass-ui's `useAnimatedNumber`
// wrapper) so the bar fills with spring/hysteresis dynamics rather than
// a linear ramp. The 0..1 output scales up to 0..100 for the gradient-
// variant Progress `modelValue` (see `currentPhaseProgressPct` below).
const phaseProgressOpts = {
    mode: "progress",
    damping: DAMPING.domProgress,
    snapThreshold: SNAP_THRESHOLD.domProgress,
} as const;

// W5-L1 — the under-meter bar is one channel of the unified phase
// handoff. The old `resetPhaseProgress(0)` zero-SNAP at every boundary
// (the jerkiest channel of the five — A4 §3) is RETIRED. Instead the bar
// reads the shared `usePhaseTransition` clock: at a boundary it DRAINS
// `current → 0` over the first third of the ≈600ms handoff, then fills
// for the new phase — the same three-beat shape the needle plays, on
// the same clock. A drain reads as "that phase is done"; a snap read as
// a glitch.
const handoff = usePhaseTransition();

// The retreat origin — the bar's smoothed fill captured at the instant a
// handoff begins, so the drain starts from exactly where the bar stood.
const drainFrom = ref(0);

// The value the bar smoother chases. Between boundaries it is the live
// per-phase progress. During a handoff it is the handoff-shaped target:
// the first third drains `drainFrom → 0`, the rest tracks the new
// phase's progress. The handoff clock is already eased, so the smoother
// follows it faithfully without fighting it.
const phaseBarTarget = computed<number>(() => {
    if (handoff.active.value && handoff.fromPhase.value != null) {
        // AV.W11b — the bar's DRAIN is the SAME two-segment phase-handoff
        // shape the needle plays (`useMeterRenderer`); both consume the one
        // shared `handoffRemap` helper + its single `1/3` split fraction so
        // the two channels can never drift. The bar DRAINS `drainFrom → 0`
        // over the first third, then fills `0 → liveProgress` over the rest.
        // T2 — derive progress from the store directly (prop-chain deleted);
        // clamp to 0..1 at this boundary (the helper takes the raw target).
        const liveProgress = clamp(
            speedtest.getSpeedtestStateAmount(
                speedtest.currentEnginePhase ?? "ping",
                "Progress",
            ),
            0,
            1,
        );
        return handoffRemap(
            handoff.progress.value,
            drainFrom.value,
            liveProgress,
        );
    }
    // T2 — store-derived progress
    return speedtest.currentEnginePhase
        ? speedtest.getSpeedtestStateAmount(
              speedtest.currentEnginePhase,
              "Progress",
          )
        : 0;
});

const { current: phaseProgressSmoothed } = useAnimatedNumber(
    () => phaseBarTarget.value,
    phaseProgressOpts,
);

// Capture the drain origin on the rising edge of each handoff — the
// bar's smoothed fill at that instant. A watcher (not an in-computed
// read) so `phaseBarTarget` never reads its own downstream value.
watch(
    () => handoff.active.value,
    (active) => {
        if (active) drainFrom.value = phaseProgressSmoothed.value;
    },
);
// Gradient-variant Progress consumes `modelValue` as a 0..100
// percentage; map the 0..1 smoother output up to that range.
const currentPhaseProgressPct = computed(() =>
    Math.max(0, Math.min(100, phaseProgressSmoothed.value * 100)),
);
</script>

<style scoped>
/* ─────────────────────── Meter column ───────────────────────
   Local-appearance rules live here; cross-cell grid placement +
   dial-out lanes stay at the composition root so the
   `.results-card .meter-col` compound selectors live in one place
   (Vue scoped CSS stamps the child root with the parent's `data-v-*`
   attribute, so the parent rules still match against this root). */
.meter-col {
    position: relative;
    /* The column shrink-wraps to the meter's ACTUAL rendered width.
       `--meter-col-target` is the size budget for the dial square: the
       running-state default is `--meter-size` (the column-share budget),
       and the composition root overrides it to `--meter-size-idle` at
       `data-engine-state="idle"` so the AG-D1 / A3§P1 Option-2
       dial-as-protagonist idle posture lets the dial claim the wider
       card-width budget. Because `.meter-inner` resolves its square side
       from the SAME `--meter-col-target` expression, the column and the
       dial canvas stay exactly the same width, so the under-meter
       `.phase-progress` keeps its clean percentage of the dial diameter
       (see `.phase-progress` below). The grid cell is
       `minmax(0, --meter-size)` on the running trio; `100%` here resolves
       against the cell, so the column never exceeds its track. At idle
       the meter cell spans `1 / -1` (set at the composition root) so the
       wider `--meter-size-idle` has room to land.

       Routing the budget through one custom prop — rather than a
       `width:` override — means the idle→running narrowing is a single
       CSS `width` transition on this property's resolved value; the grid
       template columns never change, so there is no track reflow / CLS.

       ── AV.W22.2 idle-hero dial budget ───────────────────────────────────
       At idle the dial is the at-rest PROTAGONIST: it consumes its FULL
       `--meter-size-idle` budget (witnessed — the dial side === the resolved
       budget at every wide viewport: 472px @1920, 473px @1440; see
       `audit/w22-witness.md`) and centres in the card as the breathing
       signature (`idleSweep`, untouched). The dial-budget plumbing here is the
       W22.2 carve's locus: `.meter-inner` resolves its square side from the
       SAME `--meter-col-target`, so the dial GROWS into whatever budget the
       composition root hands it — no MeterColumn-local cap clips it below the
       budget. The budget itself (`--meter-size-idle`, design-home) is
       height-band-derived, so on a WIDE-but-short viewport the square dial is
       height-bound and leaves horizontal field around it; reducing that
       at-rest field with the wordmark/"Ready" typographic protagonist is the
       idle-hero STAGE REGION, owned by the composition root (W22.1,
       `SpeedtestResults.vue`) — disjoint from this MeterColumn carve, which
       owns only the dial's budget consumption + centred at-rest posture. */
    --meter-col-target: var(--meter-size);
    width: min(var(--meter-col-target), 100%);
    max-width: 100%;
    justify-self: center;
    align-self: center;
    display: flex;
    flex-direction: column;
    align-items: center;
    min-width: 0;
    /* T5 / AUA-F7 off-screen park — `content-visibility: auto` makes the
       browser SKIP rendering this column's subtree once it scrolls out of
       the viewport, and (the operative half here) fires
       `contentvisibilityautostatechange` on the boundary so the JS can
       suspend the canvas breath loop (see `onContentVisibilityChange` in
       `<script setup>`). `contain-intrinsic-size: auto <budget>` reserves
       the column's last-rendered size while skipped so the scroll
       container does not collapse — the dial is a square of side
       `--meter-col-target` (`.meter-inner` `aspect-ratio: 1`), so that is
       the placeholder budget. The `auto` keyword caches the real measured
       size after the first render, so the reserve is exact once seen; the
       `--meter-size` term is only the pre-first-render estimate. No CLS:
       the skipped column holds its footprint. */
    content-visibility: auto;
    contain-intrinsic-size: auto var(--meter-col-target);
    /* AJ-W3-α — the under-meter bar NESTS into the dial's unpainted ring
       lane via `--meter-progress-inset` (negative margin-block-start at
       `.phase-progress` below). The column `gap` retires: with a negative
       inset on the bar, a positive column gap would fight the inset for
       no semantic gain. Per FD2 §6 DISCHARGE-3 + G-AJ-D8 ratification
       (Progress-bar composition = nest into painted-disc bound). */
    gap: 0;
    /* Dial-out lane: at rest the meter is fully opaque and
       untranslated. On `[data-complete="true"]` (set at the composition
       root) the column slides left and fades. Explicit
       transition-property list rather than `all` so unrelated
       properties like `max-width` don't ride the same easing.

       `width` rides the list too — the AG-D1 idle→running reveal narrows
       the protagonist dial from `--meter-size-idle` back to its
       running-state `--meter-size` column share. The dial resize is a
       pure CSS interpolation of the resolved `width`; it shares the
       slow / `--ease-standard` departure register with the dial-out so
       the instrument's size changes always read as one motion vocabulary
       (DDR-AV-EASING: an EXIT rides a no-overshoot bezier, never a spring). */
    opacity: 1;
    transform: translateX(0);
    /* DEPARTURE register: the dial-out leaves the stage on `[data-complete="true"]`
       (the dial resize on the AG-D1 protagonist→running narrowing also rides this
       leave-the-position register). The departure rides `--ease-standard` — the
       glass-ui exit/surface bezier (the §6 easing doctrine: an EXIT must ride a
       bezier with NO overshoot, never a spring; bouncing a leave reads as a glitch,
       overshooting a position the instrument is leaving). The iOS arrival/snap
       register (--spring-bouncy + --spring-snappy) is reserved for hero arrival +
       micro-interactions; keep distinct. */
    transition:
        opacity var(--duration-slow) var(--ease-standard),
        transform var(--duration-slow) var(--ease-standard),
        width var(--duration-slow) var(--ease-standard);
    will-change: opacity, transform;
}

.meter-inner {
    position: relative;
    /* Square canvas: the dial's geometric ceiling is `originY - r`, and
       for any square (`h === w`) the outer ring's `outerRadius` is
       strictly < `h / 2`, so the upper arc can never clip. A non-square
       wrapper with `h < w` would clip it — hence the locked 1/1 ratio.

       The square's side is `--meter-col-target` directly — the honest,
       viewport-derived budget (`100dvh − docks − chrome-reserve`,
       capped by the column width via the `100%` arm). The target is
       `--meter-size` while running and `--meter-size-idle` at idle (the
       composition root overrides the prop on `.meter-col`), so the dial
       canvas tracks the column width across the AG-D1 protagonist→column
       resize in lockstep. No `× 0.5` chassis magic: the reserve in
       `--meter-size` already accounts for the regions that share the
       meter's column. `inline-size` drives; `block-size` follows from
       `aspect-ratio`, so the dial is as large as its column and vertical
       band allow without clipping or overflowing. */
    aspect-ratio: 1 / 1;
    inline-size: min(var(--meter-col-target), 100%);
    block-size: auto;
    margin-inline: auto;
}

.speedtest {
    overflow: hidden;
    /* Inherit the meter-col cap: when the parent max-block-size clamps
       below width, this max-block-size: 100% defers to the parent so
       the inner box collapses with the wrapper rather than overflowing. */
    max-block-size: 100%;
}

/* AK-FD1 §2.3 — running-state placeholder. Fills the dial's disc lane
   during the phase-start → first-data gap with glass-ui's breath-variant
   skeleton. The circular `border-radius` matches the dial so the breath
   reads as the instrument waking, not a stray rounded-rect overlay; it
   pins to the meter-inner square (which already carries `aspect-ratio:
   1`) so the breath fills exactly the dial bounds. Sits above the canvas
   (`z-index: 1`) so the pre-data frame is the breath, not the blank rail.
   The breath's opacity register is muted so it reads as a quiet "resolving"
   surface rather than a loud loading block; the trough keeps the dial lane
   legible. `pointer-events: none` keeps it inert. The Skeleton's own PRM
   bracket pins the breath to its trough under reduced-motion. */
.meter-prewarm {
    position: absolute;
    inset: 0;
    z-index: 1;
    border-radius: 50%;
    pointer-events: none;
    /* Reuse the canon breath tokens at a quieter register than the 0.55–
       0.95 default so the dial lane stays legible beneath the swell. */
    --pulse-aura-opacity-min: 0.22;
    --pulse-aura-opacity-max: 0.5;
}

/* Phase-progress rail — the under-meter cue. It composes with the dial
   as the cap of the instrument, not a distinct row.

   Width — the bar spans the dial's actual painted DIAMETER, not the
   full (wider) column. `.meter-col` shrink-wraps to the dial canvas
   width, so `var(--dial-diameter-ratio) * 100%` resolves to exactly
   `2 · outerRadius`. The ratio is bound by an inline style var on
   `.meter-col` from `DIAL_DIAMETER_RATIO` in `canvas/meter/config.ts`
   (derived there from the same `base`-anchored ring geometry) — change
   a ring coefficient and the bar tracks. Because the dial's painted
   outer edge sits a further `outerLineWidth/2` beyond `outerRadius`,
   the bar naturally rests slightly INSIDE the dial's visual extent —
   the "slight padding" the user asked for, earned from the geometry
   rather than a magic inset.

   Thickness — slimmer than before (user 2026-05-17: the bar should be
   smaller). Tracks `--meter-size` so it scales with the dial but stays
   a hairline cap; floored so it never vanishes on tiny meters.

   The dial-to-bar gap is the single `.meter-col` column `gap` — no
   per-element margin here.

   Glass-ui's `--progress-track` paints the at-rest substrate; the
   consumer override sets it to the same alpha-22 muted-foreground that
   `rings.ts` paints the phantom tracks at, so the bar and the dial
   read as one piece of grey instrument substrate when idle. The
   `--progress-fill` gradient takes over once a phase activates. */
.phase-progress {
    width: calc(var(--dial-diameter-ratio, 0.875) * 100%);
    /* D2 — REVERSAL of AJ wash thickness DDR (AN R2-core).
       ──────────────────────────────────────────────────────────────────
       WHAT: multiplier 0.014 → 0.028 (~13px at the default --meter-size);
             floor raised from 0.1875rem → 0.25rem so the bar never vanishes.
       WHY: AJ.W3-α FD2 §4.2 S3 set 0.014 as the "hairline cap" rationale —
            correct for the AJ nest-inside-dial context, but the intent was
            "smaller than the primary bus", NOT "hairline". The DESIGN.md
            §"Phase bus" canon says "never a hairline" (DESIGN.md:552). The
            AJ suppression went below the documented floor. This restores the
            documented ~13px target from the DESIGN.md instrument-cluster spec
            and the v2 CORE prototype (core/SPEC.md §3 D2).
       ALTERNATIVE REJECTED: keep 0.014 and increase the floor — the floor
            alone can't reach ~13px without growing to a constant that breaks
            tiny-meter scaling. The multiplier is the correct knob.
       RE-LITIGATE-IF: a future UX audit finds the 13px bar competes visually
            with the baseline rail and a ~10px midpoint is preferred; reduce
            multiplier to ~0.022 and re-measure the two-frequency read.
       REVERSAL of AJ wash thickness DDR. */
    block-size: max(0.25rem, calc(var(--meter-size, 200px) * 0.028));
    /* D12 — tighten dial ↔ under-dial-bar gap (AN R2-core refinement).
       The negative inset nests the bar INTO the dial's painted-disc bound.
       glass-ui's canonical `--meter-progress-inset` token = 4% of --meter-size
       (AK.W3 transposition → glass-ui/src/styles/tokens.css:1244). At the new D2
       thickness (~13px) the 4% gap reads as a loose float below the gauge arc.
       Override the token to 5% at this scope (measured: 4% → -18.9px; 5% → -23.7px;
       7% was tried but at 473px dial size = -33px = too buried). 5% → bar visibly
       rides the arc base, 1.8× bar height overlap, no arc collision at idle. */
    /* D12 tuned: 5% of --meter-size at 1440px viewport = ~23.7px inset.
       Measured: 4% (prior) → -18.9px gap; 7% → -33px (too aggressive, bar buried).
       5% → -23.7px: bar visibly rides the arc base, 1.8× bar height overlap, no collision. */
    /* AS-13 (DDR amends AN-D12) — the user reads the current-phase bar as
       still sitting low under the dial. Lift it modestly: 5% → 6% of
       --meter-size (~28.4px at the 1440px dial size). 6% stays well short
       of the 7% the D12 measurement found "too buried" — it tucks the bar
       a touch further into the painted-disc ring lane without arc collision. */
    --meter-progress-inset: calc(var(--meter-size, 200px) * 0.06);
    margin-block-start: calc(-1 * var(--meter-progress-inset));
    border-radius: var(--radius-pill);
    --progress-track: color-mix(in srgb, var(--foreground) 22%, transparent);
    /* `--progress-fill` is a static CSS expression keyed off the chassis
       `--phase-color` cascade (`<InstrumentChassis data-phase>` retints it
       per active phase; idle resolves to `--muted-foreground`). The 0–80%
       body is the flat phase colour; the 80–100% tail brightens toward
       white via `color-mix`, baking a leading-edge "zap" glow into the
       fill itself. The indicator div's `transform: translateX(-(100% -
       value))` slides the gradient under its own `overflow: hidden` mask,
       so the bright stop sits at the active edge without any keyframe
       pulse — and with zero fill width at idle the colour never reads as
       a false start. */
    /* W17 (D3/D8) — the under-dial bar FILLS with the live phase colour.
       ──────────────────────────────────────────────────────────────────
       THE FILL BUG the user named ("never fills with color and is
       errored"), verified P0 on the real edge:

       glass-ui's `ProgressGradient` paints the indicator's fill ONLY via
       the Tailwind arbitrary utility `[background:var(--progress-fill,
       var(--primary))]` (the `background` shorthand). TWO defects stacked:
         1. SCAN GAP — that arbitrary class lives in the PUBLISHED dist's
            compiled JS (`dist/Progress-*.js`), which glass-ui's transitive
            `@source "../components"` does NOT scan (it resolves to
            `dist/components/`, `.d.ts` stubs only). So the utility was
            never generated and the indicator had NO base fill. FIXED in
            the design home (`@source` over the installed dist JS).
         2. CRESCENDO CLOBBER — glass-ui's scoped W4 crescendo overlay
            (`.progress-gradient-rail[data-lifecycle="progressing"]
            [data-state]`, specificity 0,3,1) sets `background-image`
            (longhand) to a TRANSPARENT screen-blend gradient. That
            longhand out-ranks the base utility's `background` shorthand
            (0,1,0), so a GRADIENT `--progress-fill` lands on
            `background-image` and is overwritten → transparent bar. The
            `:disable-crescendo` opt-out is a DEAD PROP at glass-ui 3.10.0
            (removed in a bump — published-dep-drift); the crescendo rule
            always matches at value 5-100. Routed: ASK-GU-PROGRESS-
            CRESCENDO-CLOBBER (the crescendo must compose as a SECOND
            background layer, not replace the fill).

       THE INTERIM (shipped-surface, restraint-slate compliant): make
       `--progress-fill` a FLAT SOLID `var(--phase-color)` rather than a
       gradient. A solid value on the `background` shorthand lands on the
       indicator's `background-COLOR`; the crescendo's
       `background-blend-mode: screen` then blends its transparent image
       OVER that colour, so the phase colour shows through and the bar
       fills. This is the flat-token-driven interim the spec mandates
       ("interim fills stay FLAT-token-driven (`--progress-fill`); NEVER
       hand-roll a local specular fill") — the leading-edge "zap"/liquid
       treatment is the routed ASK-GU-LIQUIDFILL + the crescendo ask,
       consumed on publish.

       The `--phase-color` cascade already cross-fades at the chassis
       (`@property` typed-color animation, clocked to
       `--motion-duration-phase-handoff`); reading it directly here means
       the solid fill's hue cross-fades continuously across phase
       boundaries with no width-drain/refill. */
    --progress-fill: var(--phase-color, currentColor);

    /* AE.W1 Cluster C (W1-D10–D15): the gradient indicator slides under
       its own mask via `transform: translateX(-(100% - value))`. At low
       fill the indicator's box is translated far negative — painted
       geometry that would otherwise inflate every ancestor's
       `scrollWidth` (the card measured +5–10px of horizontal overflow).
       The `Progress` primitive's `overflow: hidden` masks the indicator
       VISUALLY but does not remove its translated layout extent from
       ancestor scroll-extent computation. `contain: paint` is the
       precise primitive: it establishes a containment context so the
       translated indicator is clipped to this box AND removed from any
       ancestor's scroll/overflow calculation — strictly stronger than
       the `overflow: hidden` mask. Pairs with `overflow: clip` so the
       `.phase-progress` box itself is a hard clip boundary that can
       never become a scroll container. */
    contain: paint;
    overflow: clip;
}
</style>
